"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { monthsAndDaysBetween } from "@/lib/tool-kit/date-math/month-diff";

const MS_PER_DAY = 86_400_000;

interface Ymd {
  y: number;
  m: number; // 1-12
  d: number;
}

function parseYmd(value: string): Ymd | null {
  const parts = value.split("-");
  if (parts.length !== 3) {
    return null;
  }
  const y = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  const d = Number.parseInt(parts[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return null;
  }
  return { y, m, d };
}

function toUtcMs(date: Ymd): number {
  return Date.UTC(date.y, date.m - 1, date.d);
}

function msToYmd(ms: number): Ymd {
  const date = new Date(ms);
  return {
    y: date.getUTCFullYear(),
    m: date.getUTCMonth() + 1,
    d: date.getUTCDate(),
  };
}

function formatYmd(date: Ymd): string {
  const monthStr = String(date.m).padStart(2, "0");
  const dayStr = String(date.d).padStart(2, "0");
  return `${date.y}-${monthStr}-${dayStr}`;
}

function isWeekend(ms: number): boolean {
  const day = new Date(ms).getUTCDay();
  return day === 0 || day === 6;
}

/** Counts weekdays after startMs up to and including endMs (startMs <= endMs). */
function countBusinessDays(startMs: number, endMs: number): number {
  let count = 0;
  for (let ms = startMs + MS_PER_DAY; ms <= endMs; ms += MS_PER_DAY) {
    if (!isWeekend(ms)) {
      count += 1;
    }
  }
  return count;
}

/** Full months + remaining days between two dates (earlier first); see month-diff.ts. */
function diffMonthsDays(from: Ymd, to: Ymd): { months: number; days: number } {
  return monthsAndDaysBetween(from, to) ?? { months: 0, days: 0 };
}

function addBusinessDays(startMs: number, count: number): number {
  const step = count >= 0 ? MS_PER_DAY : -MS_PER_DAY;
  let remaining = Math.abs(count);
  let ms = startMs;
  while (remaining > 0) {
    ms += step;
    if (!isWeekend(ms)) {
      remaining -= 1;
    }
  }
  return ms;
}

interface DifferenceResult {
  totalDays: number;
  weeks: number;
  weekDays: number;
  months: number;
  monthDays: number;
  businessDays: number;
}

function computeDifference(a: Ymd, b: Ymd): DifferenceResult {
  const aMs = toUtcMs(a);
  const bMs = toUtcMs(b);
  const startMs = Math.min(aMs, bMs);
  const endMs = Math.max(aMs, bMs);
  const earlier = aMs <= bMs ? a : b;
  const later = aMs <= bMs ? b : a;
  const totalDays = Math.round((endMs - startMs) / MS_PER_DAY);
  const monthsDays = diffMonthsDays(earlier, later);
  return {
    totalDays,
    weeks: Math.floor(totalDays / 7),
    weekDays: totalDays % 7,
    months: monthsDays.months,
    monthDays: monthsDays.days,
    businessDays: countBusinessDays(startMs, endMs),
  };
}

interface AddResult {
  date: string;
  weekday: string;
}

function computeAdd(
  base: Ymd,
  amount: number,
  direction: "add" | "subtract",
  unit: "calendar" | "business",
  weekdayNames: readonly string[]
): AddResult {
  const signed = direction === "add" ? amount : -amount;
  const baseMs = toUtcMs(base);
  let resultMs = baseMs + signed * MS_PER_DAY;
  if (unit === "business") {
    resultMs = addBusinessDays(baseMs, signed);
  }
  return {
    date: formatYmd(msToYmd(resultMs)),
    weekday: weekdayNames[new Date(resultMs).getUTCDay()],
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

const DATE_INPUT_CLASS =
  "rounded-lg border border-border bg-background px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary";

type Mode = "difference" | "add";

/** English strings. A translated content file overrides these through `ui`. */
export const DATE_CALCULATOR_STRINGS = {
  weekdayNames: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],
  mode: "Mode",
  differenceBetweenTwoDates: "Difference between two dates",
  addOrSubtractDays: "Add or subtract days",
  differenceBetweenTwoDatesHeading: "Difference Between Two Dates",
  startDate: "Start date",
  endDate: "End date",
  totalDays: "Total days",
  businessDaysMonFri: "Business days (Mon-Fri)",
  weeksPlusDays: "Weeks + days",
  monthsPlusDays: "Months + days",
  addOrSubtractDaysHeading: "Add or Subtract Days",
  operation: "Operation",
  add: "Add",
  subtract: "Subtract",
  numberOfDays: "Number of days",
  dayType: "Day type",
  calendarDays: "Calendar days",
  businessDaysMonFriOption: "Business days (Mon-Fri)",
  resultingDate: "Resulting date",
  dayOfWeek: "Day of week",
  weeksDaysValue: "{weeks}w {days}d",
  monthsDaysValue: "{months}m {days}d",
} as const;

export default function DateCalculatorWidget() {
  const [mode, setMode] = useState<Mode>("difference");
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");
  const [baseInput, setBaseInput] = useState("");
  const [amountInput, setAmountInput] = useState("30");
  const [direction, setDirection] = useState<"add" | "subtract">("add");
  const [unit, setUnit] = useState<"calendar" | "business">("calendar");

  const strings = useToolUi(DATE_CALCULATOR_STRINGS);

  const fmt = useToolNumber();

  const startDate = parseYmd(startInput);
  const endDate = parseYmd(endInput);
  const difference =
    mode === "difference" && startDate && endDate
      ? computeDifference(startDate, endDate)
      : null;

  const baseDate = parseYmd(baseInput);
  const amount = parseIntegerInput(amountInput);
  const addResult =
    mode === "add" && baseDate && amount !== null && amount >= 0
      ? computeAdd(baseDate, amount, direction, unit, strings.weekdayNames)
      : null;

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "difference"}
              className="accent-primary"
              name="date-mode"
              onChange={() => setMode("difference")}
              type="radio"
              value="difference"
            />
            {strings.differenceBetweenTwoDates}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={mode === "add"}
              className="accent-primary"
              name="date-mode"
              onChange={() => setMode("add")}
              type="radio"
              value="add"
            />
            {strings.addOrSubtractDays}
          </label>
        </div>
      </section>

      {/* Difference mode */}
      {mode === "difference" && (
        <section className="space-y-4 rounded-lg border border-border bg-card p-6">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.differenceBetweenTwoDatesHeading}
          </h2>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-xs"
                htmlFor="start-date"
              >
                {strings.startDate}
              </label>
              <input
                className={DATE_INPUT_CLASS}
                id="start-date"
                onChange={(e) => setStartInput(e.target.value)}
                type="date"
                value={startInput}
              />
            </div>
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-xs"
                htmlFor="end-date"
              >
                {strings.endDate}
              </label>
              <input
                className={DATE_INPUT_CLASS}
                id="end-date"
                onChange={(e) => setEndInput(e.target.value)}
                type="date"
                value={endInput}
              />
            </div>
          </div>
          {difference && (
            <div className="grid grid-cols-2 gap-4">
              <StatCard
                label={strings.totalDays}
                value={fmt(difference.totalDays)}
              />
              <StatCard
                label={strings.businessDaysMonFri}
                value={fmt(difference.businessDays)}
              />
              <StatCard
                label={strings.weeksPlusDays}
                value={t(strings.weeksDaysValue, {
                  weeks: difference.weeks,
                  days: difference.weekDays,
                })}
              />
              <StatCard
                label={strings.monthsPlusDays}
                value={t(strings.monthsDaysValue, {
                  months: difference.months,
                  days: difference.monthDays,
                })}
              />
            </div>
          )}
        </section>
      )}

      {/* Add/subtract mode */}
      {mode === "add" && (
        <section className="space-y-4 rounded-lg border border-border bg-card p-6">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.addOrSubtractDaysHeading}
          </h2>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-xs"
                htmlFor="base-date"
              >
                {strings.startDate}
              </label>
              <input
                className={DATE_INPUT_CLASS}
                id="base-date"
                onChange={(e) => setBaseInput(e.target.value)}
                type="date"
                value={baseInput}
              />
            </div>
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-xs"
                htmlFor="date-direction"
              >
                {strings.operation}
              </label>
              <select
                className={DATE_INPUT_CLASS}
                id="date-direction"
                onChange={(e) =>
                  setDirection(e.target.value as "add" | "subtract")
                }
                value={direction}
              >
                <option value="add">{strings.add}</option>
                <option value="subtract">{strings.subtract}</option>
              </select>
            </div>
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-xs"
                htmlFor="date-amount"
              >
                {strings.numberOfDays}
              </label>
              <input
                className="w-28 rounded-lg border border-border bg-background px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                id="date-amount"
                min={0}
                onChange={(e) => setAmountInput(e.target.value)}
                step="1"
                type="number"
                value={amountInput}
              />
            </div>
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-xs"
                htmlFor="date-unit"
              >
                {strings.dayType}
              </label>
              <select
                className={DATE_INPUT_CLASS}
                id="date-unit"
                onChange={(e) =>
                  setUnit(e.target.value as "calendar" | "business")
                }
                value={unit}
              >
                <option value="calendar">{strings.calendarDays}</option>
                <option value="business">
                  {strings.businessDaysMonFriOption}
                </option>
              </select>
            </div>
          </div>
          {addResult && (
            <div className="grid grid-cols-2 gap-4">
              <StatCard label={strings.resultingDate} value={addResult.date} />
              <StatCard label={strings.dayOfWeek} value={addResult.weekday} />
            </div>
          )}
        </section>
      )}
    </>
  );
}
